import { RefObject, useState } from 'react'

import * as d3 from 'd3'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'

import type { BoundingBox } from 'modules/people_chart/lib/BoundingBox'
import { Dimensions } from 'modules/people_chart/lib/Dimensions'

const MIN_ZOOM = 0
const MAX_ZOOM = 1
const DEFAULT_SCALE = 1
const FIT_SCALE_MARGIN = 10
const INVERT = -1

type ReturnType = {
  value: string
  scale: (amount: number) => void
  fitToBoundingBox: (boundingBox: BoundingBox) => void
  isZoomMaxed: boolean
  reset: () => void
  isReady: boolean
}

interface ZoomEvent {
  transform: d3.ZoomTransform
}

const useZoom = (
  svgRef: RefObject<SVGSVGElement>,
  size: Dimensions,
  disableZoom = false,
  disablePanning = false
): ReturnType => {
  const [zoomState, setZoomState] = useState(d3.zoomIdentity)
  const zoom = useMemo(
    () =>
      d3
        .zoom()
        .scaleExtent([MIN_ZOOM, MAX_ZOOM])
        .filter((event) => {
          const isCtrl = event.ctrlKey
          const isUsingWheel = event.type === 'wheel'
          const isMouseButtonNotPressed = !event.button
          if (isUsingWheel) {
            return isCtrl && !disableZoom
          }

          return isMouseButtonNotPressed && !isCtrl
        }),
    [disableZoom]
  )
  const handleZoomChange = useUnsafeCallback(
    (zoomEvent: ZoomEvent) => {
      const svgElement = svgRef.current
      if (!svgElement) return
      const x = disablePanning ? zoomState.x : zoomEvent.transform.x
      const y = disablePanning ? zoomState.y : zoomEvent.transform.y
      const k = disableZoom ? zoomState.k : zoomEvent.transform.k
      const transform = d3.zoomIdentity.translate(x, y).scale(k)

      setZoomState(transform)
    },
    [svgRef, disablePanning, disableZoom, zoomState]
  )

  const scale = (amount: number) => {
    const svgElement = svgRef.current
    if (!svgElement) return
    if (disableZoom) return
    const svg = d3.select(svgElement)
    svg.call(zoom.scaleBy as any, amount)
  }

  const boundingBoxMinScale = (boundingBox: BoundingBox) => {
    const [width, height] = size
    const { x1, x0, y1, y0 } = boundingBox
    const boundingBoxWidth = x1 - x0
    const boundingBoxHeight = y1 - y0
    const scaleHorizontalFit = width / (boundingBoxWidth + 2 * FIT_SCALE_MARGIN)
    const scaleVerticalFit = height / (boundingBoxHeight + 2 * FIT_SCALE_MARGIN)
    const minScaleToZoom = Math.min(scaleHorizontalFit, scaleVerticalFit)
    const fitsInViewport = boundingBoxWidth < width && boundingBoxHeight < height
    return fitsInViewport ? DEFAULT_SCALE : minScaleToZoom
  }

  const fitToBoundingBox = (boundingBox: BoundingBox) => {
    const svgElement = svgRef.current
    if (!svgElement) return
    const svg = d3.select(svgElement)
    const [width, height] = size
    const viewportCenterX = width / 2
    const viewportCenterY = height / 2
    const { x1, x0, y1, y0 } = boundingBox
    const boundingBoxCenterX = -(x0 + x1) / 2
    const boundingBoxCenterY = -(y0 + y1) / 2

    svg.call(
      zoom.transform as any,
      d3.zoomIdentity
        .translate(viewportCenterX, viewportCenterY)
        .scale(boundingBoxMinScale(boundingBox))
        .translate(boundingBoxCenterX, boundingBoxCenterY)
    )
  }

  // TODO: This is kind of flickery needs to be improved
  const handleWheelEvent = (event: WheelEvent) => {
    const svgElement = svgRef.current
    if (!svgElement) return

    // If ctrl key is pressed, we are zooming so we don't want to translate
    if (event.ctrlKey) return

    const svg = d3.select(svgElement)
    svg.call(zoom.translateBy as any, event.deltaX * INVERT, event.deltaY * INVERT)
  }

  useUnsafeEffect(() => {
    const svgElement = svgRef.current
    if (!svgElement) return

    const svg = d3.select(svgElement)

    zoom.on('zoom', handleZoomChange)

    svg.call(zoom as any)
    svgElement.addEventListener('wheel', handleWheelEvent)

    return () => {
      zoom.on('zoom', null)
      svgElement.removeEventListener('wheel', handleWheelEvent)
    }
  }, [svgRef, handleZoomChange, zoom])

  const reset = () => {
    setZoomState(d3.zoomIdentity)
  }
  return {
    value: zoomState?.toString() || '',
    scale,
    fitToBoundingBox,
    isZoomMaxed: zoomState.k === MAX_ZOOM,
    reset,
    isReady: !!svgRef.current,
  }
}

export default useZoom
